import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SltAddDataServiceService } from '../../services/slt-add-data-service.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-slt-add-data-service',
    templateUrl: './slt-add-data-service.component.html',
    styleUrls: ['./slt-add-data-service.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltAddDataServiceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  gridData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Component specific properties
  modalTitle = 'Add/Edit Data Service';
  showFormLoader = true;
  crudLoading = false;
  loading = false;
  
  // Initialization guard to prevent multiple API calls
  private isInitialized = false;
  
  // Form data
  clients: any[] = [];
  selectedClientKeys: number[] = [];
  clientChanged = false;
  clientDropdownOpen = false;
  
  // Service output definition options
  options = [
    {name: '---SELECT---', value: ''},
    {name: 'Procedure Call', value: 'proc_call'},
    {name: 'Stored/Fixed Output', value: 'use_stored_output'}
  ];
  
  // Service data model
  serviceData: any = {
    serviceKey: 0,
    serviceName: '',
    recordLimit: '100000',
    serviceOutputDef: '',
    proc_call: '',
    proc_input: '',
    proc_output: 'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR',
    service_output: '',
    service_access_sso: '',
    selectedClients: []
  };

  // original copy
  initialServiceData : any = {};
  
  // Form validation flags
  isFormValid = false;
  formErrors: any = {
    serviceName: false,
    serviceOutputDef: false,
    proc_call: false,
    proc_input: false,
    proc_output: false,
    service_output: false
  };

  constructor(
    private sltAddDataServiceService: SltAddDataServiceService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Close dropdown when clicking outside
    this.clientDropdownOpen = false;
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.applyWorkspaceContext();
    // this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
    //   this.applyWorkspaceContext();
    //   this.init();
    // }));
    // this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
    //   this.applyWorkspaceContext();
    //   this.init();
    // }));
    if (this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.showFormLoader = false;
      this.init();
    } else {
      this.showFormLoader = true;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = g && typeof g === 'object' ? g : {};
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.isInitialized = false;
    if (this.rowData && this.rowData.SERVICE_KEY) {
      this.populateServiceData();
    }
    this.cdr.markForCheck();
  }

  /**
   * Populate service data from rowData (for edit mode)
   * This can be called multiple times as data arrives
   */
  private populateServiceData() {
    if (this.rowData && this.rowData.SERVICE_KEY) {
      console.log('Populating service data from rowData:', this.rowData.SERVICE_KEY);
      this.serviceData = {
        serviceKey: this.rowData.SERVICE_KEY,
        serviceName: this.rowData.SERVICE_NAME || '',
        recordLimit: this.rowData.RECORD_LIMIT || '100000',
        serviceOutputDef: this.rowData.USE_STORED_OUTPUT === 1 ? 'use_stored_output' : 'proc_call',
        proc_call: this.rowData.PROC_CALL || '',
        proc_input: this.rowData.PROC_PARAMS_IN || '',
        proc_output: this.rowData.PROC_PARAMS_OUT || 'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR',
        service_output: this.rowData.SERVICE_OUTPUT || '',
        service_access_sso: '',
        selectedClients: []
      };
    }
    this.initialServiceData = _.cloneDeep(this.serviceData || {});
  }

  init() {
    try {
      // Guard: Only initialize once to prevent multiple API calls from @Input setters
      if (this.isInitialized) {
        console.log('Already initialized, skipping duplicate init() call');
        return;
      }
      
      if (this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
        console.log('Initializing component (first time)');
        this.isInitialized = true; // Set flag to prevent re-initialization
        
        // Initialize service data from rowData if editing
        this.populateServiceData();
        
        // Load client list (will only happen once now)
        this.getClientList();
      }
    } catch (e) {
      console.error('Error in SltAddDataService init:', e);
      this.showFormLoader = false;
    }
  }

  getClientList() {
    this.loading = true;
    this.crudLoading = true;

    // Try to use clients from gridData if available (passed from parent)
    if (this.gridData && this.gridData.clients && this.gridData.clients.length > 0) {
      console.log('Using clients from gridData:', this.gridData.clients.length);
      this.clients = this.gridData.clients;
      this.clients.sort((a: any, b: any) => a.id - b.id);
      
      if (this.rowData && this.rowData.SERVICE_KEY) {
        this.getDataServiceClients();
      } else {
        this.loading = false;
        this.crudLoading = false;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    } else {
      // Fallback: Load from API if gridData.clients not available
      console.log('gridData.clients not available, fetching from API...');
      
      // CRITICAL: Must use admin_api_url (not base_url) to match AngularJS ClientAdminFactory
      if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.admin_api_url) {
        console.error('Cannot fetch clients: GENERAL_CONFIG.admin_api_url not available');
        this.loading = false;
        this.crudLoading = false;
        this.showFormLoader = false;
        this._snackBar.open('Configuration error: admin API URL not available', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
        return;
      }

      // Call ClientAdminFactory.getAllClients() equivalent
      // Endpoint: admin_api_url + '/getAllClients' (POST)
      this.sltAddDataServiceService.getAllClients(this.GENERAL_CONFIG.admin_api_url).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          console.log('getAllClients API response:', response);
          this.loading = false;
          this.crudLoading = false;
          
          // API response format: response.clientResult.clientList
          // (HttpClient already unwraps response.data automatically)
          if (response && response.clientResult && response.clientResult.clientList) {
            // Transform the API response to match expected format
            // Uses: client.client_key and client.client_name
            this.clients = response.clientResult.clientList.map((client: any) => ({
              id: client.client_key,
              name: client.client_name
            }));
            this.clients.sort((a: any, b: any) => a.id - b.id);
            console.log('Clients loaded from API:', this.clients.length);
            
            // If editing an existing service, load its assigned clients
            if (this.rowData && this.rowData.SERVICE_KEY) {
              this.getDataServiceClients();
            } else {
              this.showFormLoader = false;
              this.cdr.detectChanges();
            }
          } else {
            console.error('Failed to load clients from API. Response:', response);
            this._snackBar.open('Error loading client list', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.showFormLoader = false;
            this.cdr.detectChanges();
          }
        },
        (error: any) => {
          console.error('Error loading clients from API:', error);
          this.loading = false;
          this.crudLoading = false;
          this.showFormLoader = false;
          this._snackBar.open('Error loading client list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
    }
  }

  getDataServiceClients() {
    if (!this.rowData.SERVICE_KEY) {
      this.loading = false;
      this.crudLoading = false;
      this.showFormLoader = false;
      return;
    }

    this.loading = true;

    this.sltAddDataServiceService.getDataServiceClients(
      this.GENERAL_CONFIG.base_url,
      this.rowData.SERVICE_KEY
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.jsonObject) {
          let responseClients: number[] = [];
          
          // Get service output if stored output mode
          if (this.rowData.USE_STORED_OUTPUT === 1 && response.jsonObject.length > 0) {
            this.serviceData.service_output = response.jsonObject[0].SERVICE_OUTPUT;
          }
          
          // Extract client keys from response
          response.jsonObject.forEach((item: any) => responseClients.push(item.CLIENT_KEY));
          
          // Match with available clients and set as selected
          responseClients.forEach((clientKey: number) => {
            const matchedClient = this.clients.filter((client: any) => 
              parseInt(client.id) === parseInt(clientKey.toString())
            );
            
            if (matchedClient && matchedClient.length > 0) {
              this.selectedClientKeys.push(clientKey);
            }
          });
          
          // Set the selected clients array for the multi-select
          this.serviceData.selectedClients = this.selectedClientKeys;
          this.initialServiceData = _.cloneDeep(this.serviceData || {});
          
          this.showFormLoader = false;
          this.cdr.detectChanges();
        } else {
          this._snackBar.open(response.errorMessage || 'Error loading data service clients', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.showFormLoader = false;
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        this.crudLoading = false;
        this.showFormLoader = false;
        console.error('Error loading data service clients:', error);
        this._snackBar.open('Error occurred while loading clients', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  isProcCall(): boolean {
    return this.serviceData.serviceOutputDef === 'proc_call';
  }

  isStoredOutput(): boolean {
    return this.serviceData.serviceOutputDef === 'use_stored_output';
  }

  toggleClientDropdown(event: Event) {
    event.stopPropagation();
    this.clientDropdownOpen = !this.clientDropdownOpen;
    this.cdr.detectChanges();
  }

  getSelectedClientsText(): string {
    if (!this.selectedClientKeys || this.selectedClientKeys.length === 0) {
      return 'Select clients';
    }
    if (this.selectedClientKeys.length === 1) {
      const client = this.clients.find(c => parseInt(c.id) === this.selectedClientKeys[0]);
      return client ? client.name : '1 selected';
    }
    return `${this.selectedClientKeys.length} selected`;
  }

  isClientSelected(client: any): boolean {
    return this.selectedClientKeys.some(id => parseInt(id.toString()) === parseInt(client.id));
  }

  toggleClientSelection(client: any) {
    const clientId = parseInt(client.id);
    const index = this.selectedClientKeys.findIndex(id => parseInt(id.toString()) === clientId);
    
    if (index > -1) {
      this.selectedClientKeys.splice(index, 1);
    } else {
      this.selectedClientKeys.push(clientId);
    }
    
    this.clientChanged = true;
    this.validateForm();
    this.cdr.detectChanges();
  }

  selectAllClients() {
    this.selectedClientKeys = this.clients.map(c => parseInt(c.id));
    this.clientChanged = true;
    this.validateForm();
    this.cdr.detectChanges();
  }

  unselectAllClients() {
    this.selectedClientKeys = [];
    this.clientChanged = true;
    this.validateForm();
    this.cdr.detectChanges();
  }

  validateForm(): boolean {
    // Reset errors
    this.formErrors = {
      serviceName: false,
      serviceOutputDef: false,
      proc_call: false,
      proc_input: false,
      proc_output: false,
      service_output: false
    };

    // Validate service name
    if (!this.serviceData.serviceName || this.serviceData.serviceName.trim() === '') {
      this.formErrors.serviceName = true;
    }

    // Validate service output definition
    if (!this.serviceData.serviceOutputDef || this.serviceData.serviceOutputDef === '') {
      this.formErrors.serviceOutputDef = true;
    }

    // Validate procedure call fields
    if (this.isProcCall()) {
      if (!this.serviceData.proc_call || this.serviceData.proc_call.trim() === '') {
        this.formErrors.proc_call = true;
      }
      if (!this.serviceData.proc_input || this.serviceData.proc_input.trim() === '') {
        this.formErrors.proc_input = true;
      }
      if (!this.serviceData.proc_output || this.serviceData.proc_output.trim() === '') {
        this.formErrors.proc_output = true;
      }
    }

    // Validate stored output field
    if (this.isStoredOutput()) {
      if (!this.serviceData.service_output || this.serviceData.service_output.trim() === '') {
        this.formErrors.service_output = true;
      }
    }

    // Check if any errors exist
    this.isFormValid = !Object.values(this.formErrors).some((error: any) => error === true);
    
    return this.isFormValid;
  }

  save() {
    // Validate form
    if (!this.validateForm()) {
      this._snackBar.open('Please fill all required fields', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    // Check if at least one client is selected
    if (this.selectedClientKeys.length === 0) {
      if (this.rowData && this.rowData.CLIENT_KEY) {
        this.selectedClientKeys.push(parseInt(this.rowData.CLIENT_KEY));
      } else {
        this._snackBar.open('Please select at least one client to create a data service API', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const params = {
      serviceKey: this.serviceData.serviceKey,
      serviceName: this.serviceData.serviceName,
      recordLimit: this.serviceData.recordLimit,
      proc_call: this.serviceData.proc_call,
      proc_input: this.serviceData.proc_input,
      proc_output: this.isStoredOutput() ? '' : this.serviceData.proc_output,
      use_stored_output: this.isStoredOutput() ? 1 : 0,
      service_output: this.serviceData.service_output,
      clients: Object.values(this.selectedClientKeys).join(","),
      service_access_sso: this.serviceData.service_access_sso
    };

    this.sltAddDataServiceService.saveDataService(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1") {
          this._snackBar.open('Data service saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error saving data service', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving data service:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    // Reset to original values from rowData or defaults
    if (this.rowData && this.rowData.SERVICE_KEY) {
      this.serviceData = {
        serviceKey: this.rowData.SERVICE_KEY,
        serviceName: this.rowData.SERVICE_NAME || '',
        recordLimit: this.rowData.RECORD_LIMIT || '100000',
        serviceOutputDef: this.rowData.USE_STORED_OUTPUT === 1 ? 'use_stored_output' : 'proc_call',
        proc_call: this.rowData.PROC_CALL || '',
        proc_input: this.rowData.PROC_PARAMS_IN || '',
        proc_output: this.rowData.PROC_PARAMS_OUT || 'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR',
        service_output: this.rowData.SERVICE_OUTPUT || '',
        service_access_sso: '',
        selectedClients: []
      };
      this.selectedClientKeys = [];
      // Reload the original selected clients
      this.getDataServiceClients();
    } else {
      this.serviceData = {
        serviceKey: 0,
        serviceName: '',
        recordLimit: '100000',
        serviceOutputDef: '',
        proc_call: '',
        proc_input: '',
        proc_output: 'resultset:OracleTypes.CURSOR,retval:OracleTypes.VARCHAR,error_out:OracleTypes.VARCHAR',
        service_output: '',
        service_access_sso: '',
        selectedClients: []
      };
      this.selectedClientKeys = [];
    }
    
    // Reset form errors
    this.formErrors = {
      serviceName: false,
      serviceOutputDef: false,
      proc_call: false,
      proc_input: false,
      proc_output: false,
      service_output: false
    };
    
    this.cdr.detectChanges();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}
